import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '../@types/AsyncStatus';
import type { RootState } from '../store/store';
import { JWTHeader } from '../utils/JWTHeader';

export type YearOption = { value: string; label: string };

interface UseDvfYearsOptions {
  startYear?: number; // inclusive start year for options, defaults to 2014
}

export function useDvfYears(options: UseDvfYearsOptions = {}) {
  const { startYear = 2014 } = options;

  const jwt = useSelector((state: RootState) => state.auth.token);

  const [yearsOptions, setYearsOptions] = useState<YearOption[]>([]);
  const [lastYear, setLastYear] = useState<number | null>(null);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jwt) { return; }
    let aborted = false;
    const run = async() => {
      try {
        setStatus('PENDING');
        const response = await fetch('/api/dvf/last-date', JWTHeader(jwt));
        if (!response.ok) {
          throw new Error('Failed to fetch last DVF date');
        }
        const dataDate = await response.json();
        const last = new Date(dataDate.date).getFullYear();
        if (aborted) { return; }
        setLastYear(last);
        const length = Math.max(0, last - startYear + 1);
        const opts: YearOption[] = Array.from({ length }, (_, i) => {
          const y = last - i; // descending from last to startYear
          return { value: y.toString(), label: y.toString() };
        });
        setYearsOptions(opts);
        setStatus('SUCCESS');
      } catch (e) {
        if (aborted) { return; }
        setError(e instanceof Error ? e.message : 'Unknown error');
        setStatus('ERROR');
      }
    };
    run();
    return () => { aborted = true; };
  }, [jwt, startYear]);

  const getDefaultSelection = useCallback((count: number): YearOption[] => {
    return yearsOptions.slice(0, Math.max(0, count));
  }, [yearsOptions]);

  const yearsAsNumbers = useMemo(() => yearsOptions.map((o) => Number(o.value)), [yearsOptions]);

  return { yearsOptions, yearsAsNumbers, lastYear, status, error, getDefaultSelection };
}

export default useDvfYears;


